import { gatherRolesFromScoped, getScopedClaims } from "./auth0Claims.js";

const ROLE_KEY_PATTERN = /role/i;

const DEVELOPER_ROLE_ALIASES = new Set([
  'developeradmin',
  'developer_admin',
  'dev_admin',
  'devadmin',
  'developer_administrator',
  'developeradministrator',
  'developer',
  'privana_owner',
  'privanaowner',
  'privana_admin',
  'privanaadmin',
  'platform_admin',
  'platformadmin',
  'platform_owner',
  'platformowner',
  'privana',
]);

const CLUB_ROLE_ALIASES = new Set([
  'club_user',
  'clubuser',
  'user',
  'member',
  'club_member',
  'clubmember',
  'club_staff',
  'clubstaff',
  'tenant_user',
  'tenantuser',
  'tenant_member',
  'tenantmember',
  'staff',
]);

export function normalizeRole(role) {
  if (!role) return '';
  const str = String(role).trim();
  if (!str) return '';

  const withWordBoundaries = str
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s/\\:-]+/g, '_')
    .replace(/__+/g, '_');

  const sanitized = withWordBoundaries
    .replace(/[^a-z0-9_]+/gi, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

  if (!sanitized) return '';

  if (
    DEVELOPER_ROLE_ALIASES.has(sanitized) ||
    (sanitized.includes('developer') && sanitized.includes('admin')) ||
    (sanitized.includes('privana') && sanitized.includes('owner')) ||
    (sanitized.includes('privana') && sanitized.includes('admin')) ||
    sanitized === 'owner'
  ) {
    return 'developer_admin';
  }

  if (
    CLUB_ROLE_ALIASES.has(sanitized) ||
    sanitized === 'user' ||
    sanitized === 'member' ||
    (sanitized.includes('club') && (sanitized.includes('user') || sanitized.includes('member') || sanitized.includes('staff')))
  ) {
    return 'club_user';
  }

  return sanitized;
}

function collectStringCandidate(collected, value) {
  if (value == null) return;
  const str = String(value).trim();
  if (str) {
    collected.add(str);
  }
}

function walkValue(collected, value, visited, shouldCollectPrimitives = false) {
  if (value == null) {
    return;
  }

  if (typeof value !== 'object') {
    if (shouldCollectPrimitives) {
      collectStringCandidate(collected, value);
    }
    return;
  }

  if (visited.has(value)) return;
  visited.add(value);

  if (Array.isArray(value)) {
    value.forEach((entry) => {
      walkValue(collected, entry, visited, shouldCollectPrimitives);
    });
    return;
  }

  for (const [key, child] of Object.entries(value)) {
    if (child == null) continue;
    const collectForChild = shouldCollectPrimitives || ROLE_KEY_PATTERN.test(key.toLowerCase());

    if (typeof child === 'object') {
      walkValue(collected, child, visited, collectForChild);
    } else if (collectForChild) {
      collectStringCandidate(collected, child);
    }
  }
}

export function gatherRoleCandidates(...sources) {
  const collected = new Set();
  const visited = new WeakSet();

  const scan = (value) => {
    if (value == null) return;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      collectStringCandidate(collected, value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(scan);
      return;
    }

    if (typeof value === 'object') {
      walkValue(collected, value, visited, false);
    }
  };

  sources.forEach(scan);

  return Array.from(collected);
}

export function resolveRoleContext({
  user,
  userProfile,
  auth0User,
  auth0Claims,
  additionalRoles = [],
  scopedAuth0Claims,
  scopedUserClaims,
  scopedProfileClaims,
} = {}) {
  const scopedFromAuth0 = scopedAuth0Claims || getScopedClaims(auth0User);
  const scopedFromUser = scopedUserClaims || getScopedClaims(user);
  const scopedFromProfile = scopedProfileClaims || getScopedClaims(userProfile);
  const scopedFromClaims = getScopedClaims(auth0Claims);

  const scopedSources = [
    scopedFromAuth0,
    scopedFromUser,
    scopedFromProfile,
    scopedFromClaims,
  ].filter((value) => value && typeof value === 'object');

  const scopedRoles = scopedSources.flatMap((scoped) => gatherRolesFromScoped(scoped));

  const rawCandidates = [
    userProfile?.role,
    userProfile?.platform_role,
    userProfile?.roleCategory,
    userProfile?.role_category,
    user?.role,
    user?.roleCategory,
    user?.role_category,
    user?.platform_role,
    user?.platformRole,
    auth0User?.role,
    auth0User?.platform_role,
    auth0User?.platformRole,
    auth0User?.roleCategory,
    auth0User?.role_category,
    ...additionalRoles,
    ...scopedRoles,
    ...(user?.is_developer_admin ? ['developer_admin'] : []),
    ...(user?.isDeveloperAdmin ? ['developer_admin'] : []),
    ...(user?.is_developer ? ['developer_admin'] : []),
    ...(userProfile?.is_developer_admin ? ['developer_admin'] : []),
    ...(userProfile?.isDeveloperAdmin ? ['developer_admin'] : []),
  ];

  const gathered = gatherRoleCandidates(
    userProfile,
    auth0User,
    user,
    scopedFromAuth0,
    scopedFromUser,
    scopedFromProfile,
    scopedFromClaims,
    auth0Claims,
  );

  const normalizedRoles = [];
  const seen = new Set();

  [...rawCandidates, ...gathered].forEach((candidate) => {
    const normalized = normalizeRole(candidate);
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      normalizedRoles.push(normalized);
    }
  });

  const platformRole =
    normalizedRoles.find((role) => role === 'developer_admin') ||
    normalizedRoles.find((role) => role === 'club_user') ||
    normalizedRoles[0] ||
    null;

  return {
    platformRole,
    normalizedRoles,
    isDeveloperAdmin: platformRole === 'developer_admin',
  };
}

export function normalizeModules(mods) {
  if (!mods) return [];
  if (Array.isArray(mods)) return mods.map((m) => String(m).trim()).filter(Boolean);
  if (typeof mods === 'string') {
    return mods
      .split(/[\s,]+/)
      .map((m) => m.trim())
      .filter(Boolean);
  }
  if (typeof mods === 'object') {
    return Object.entries(mods)
      .filter(([, enabled]) => enabled !== false && enabled != null)
      .map(([key]) => String(key).trim())
      .filter(Boolean);
  }
  return [];
}

export function normalizeClub(club) {
  if (!club) return null;
  return {
    ...club,
    enabled_modules: normalizeModules(club.enabled_modules || club.modules),
  };
}

const CLUB_PATH_PATTERN = /^\/\d{6}(\/|$)/;

function resolvePathname(candidate) {
  if (typeof candidate === 'string') return candidate;
  if (typeof window !== 'undefined') {
    try {
      return window.location?.pathname || '';
    } catch {
      return '';
    }
  }
  return '';
}

export function isClubContext(pathname) {
  const path = resolvePathname(pathname);
  return CLUB_PATH_PATTERN.test(path);
}

export function isPlatformContext(pathname) {
  return !isClubContext(pathname);
}
